import React, { memo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, ListItem, Tooltip, useTheme } from '@mui/material';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import { uncompletedLoanErrorMessageParams } from 'app/pages/loanApplication/LoanApplication.constants';
import LazyWrapper from 'app/shared/components/common/LazyWrapper';
import UploadDocumentsModal from 'app/shared/components/document/UploadDocumentsModal';
import LabelCDS from 'app/shared/componentsRestyled/coreDesignSystem/LabelCDS';
import {
  LabelSize,
  LabelVariant,
} from 'app/shared/componentsRestyled/coreDesignSystem/LabelCDS/LabelCDS.constants';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { DOCUMENT_REJECTED_PLACEHOLDER } from 'constants/documents';
import { DOCUMENT_FILE_TYPE } from 'constants/file';
import { PAGE_CODES } from 'constants/pageCodes';
import { checkIsDocumentUndeletable, checkIsRecipientCopy } from 'utils/documents';
import { compressFile } from 'utils/file';
import { jsonStringify } from 'utils/json';

import { getMasterPermissionsToPage } from 'store/auth/selectors';
import { uploadDocumentAction } from 'store/documents/actions';
import { openDialogAction, showMessageAction } from 'store/global/actions';
import { showMessageThatCurrentUserCantChangeLoanAction } from 'store/loanApplications/actions';
import {
  selectIsCurrentUserCanChangeLoan,
  selectIsLoanCanceled,
  selectIsLoanInitiatedWithErrors,
} from 'store/loanApplications/selectors';
import { getDocumentTypes } from 'store/system/selectors';

import {
  DOCUMENT_LABEL_TOOLTIP_POPPER_PROPS,
  LoanDocumentsTreeNodeType,
} from '../../LoanApplicationDocuments.constants';
import { LoanApplicationDocumentsTestIds } from '../../LoanApplicationDocuments.testIds';

import DocumentItem from './DocumentItem';
import { getStyles } from './DocumentTypeItem.styles';
import { groupRecipientCopiesByMaster } from './DocumentTypeItem.utils';

const defaultArray = [];

const UPLOAD_FILE_ALLOWED_FORMATS = [
  DOCUMENT_FILE_TYPE.PDF,
  DOCUMENT_FILE_TYPE.PNG,
  DOCUMENT_FILE_TYPE.JPEG,
  DOCUMENT_FILE_TYPE.JSON,
];

const DocumentTypeItem = ({
  items = defaultArray,
  expandedList,
  documentId = '',
  loanParams,
  onDocumentItemClick,
  documentItemsToMerge,
  onDragStart,
  onDragEnd,
  setDraggableData,
  onDragOver,
  onDragLeave,
  isDragging,
  isEmptyTreeItemVisible,
  onCustomNavigateDocument,
  onOpenDocumentItemContextMenu,
  onToggleRecipientCopies,
  isCopyDocumentsAllowed,
}) => {
  const theme = useTheme();
  const classes = getStyles(theme);
  const dispatch = useDispatch();

  const { isManageAllowed, isEditAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.DOCUMENTS),
  );
  const documentTypes = useSelector(getDocumentTypes);
  const isLoanInitedWithErrors = useSelector(selectIsLoanInitiatedWithErrors);
  const isCurrentUserCanChangeLoan = useSelector(selectIsCurrentUserCanChangeLoan);
  const isLoanCanceled = useSelector(selectIsLoanCanceled);

  const getTreeItemSx = useCallback(
    hasSelectedDoc => {
      const selectedStyles = hasSelectedDoc ? classes.hasSelected : {};

      return {
        ...classes.root,
        '& > .MuiTreeItem-content': classes.content,
        '& .MuiTreeItem-label': {
          ...classes.typeLabel,
          ...selectedStyles,
        },
        '& .MuiTreeItem-iconContainer': {
          ...classes.iconContainer,
          ...classes.typeIcon,
          ...selectedStyles,
        },
        '& > .MuiTreeItem-groupTransition': classes.group,
      };
    },
    [classes],
  );

  const getUploadFileFunction = useCallback(
    documentTypeId => file =>
      compressFile(file)
        .then(file => {
          const body = {
            DocumentTypeId: documentTypeId,
            FileName: file.name,
            ContentType: file.type,
            Metadata: {
              LoanId: loanParams.loanIdOrLegacyLoanNumber,
              ApplicationId: loanParams.applicationId,
              IsManuallyCreated: true,
            },
          };

          const fd = new FormData();
          fd.append('file', file, file.name);
          fd.append('body', jsonStringify(body));

          return dispatch(uploadDocumentAction(fd));
        })
        .catch(({ message }) => dispatch(showMessageAction({ message, variant: 'error' }))),
    [loanParams, dispatch],
  );

  const onOpenUploadDialog = useCallback(
    ({ loanParams, documentTypeId, documentTypeName }) =>
      e => {
        e.stopPropagation();

        const abortController = new AbortController();

        if (isLoanInitedWithErrors) {
          return dispatch(showMessageAction(uncompletedLoanErrorMessageParams));
        }

        if (!isCurrentUserCanChangeLoan) {
          return dispatch(showMessageThatCurrentUserCantChangeLoanAction());
        }

        dispatch(
          openDialogAction({
            header: `Upload ${documentTypeName}`,
            body: dialogProps => (
              <UploadDocumentsModal
                abortController={abortController}
                documentTypeId={documentTypeId}
                documentTypeName={documentTypeName}
                loanParams={loanParams}
                uploadFile={getUploadFileFunction(documentTypeId)}
                uploadFileAllowedFormats={UPLOAD_FILE_ALLOWED_FORMATS}
                {...dialogProps}
              />
            ),
            afterCloseCallback: () => abortController.abort(),
          }),
        );
      },
    [dispatch, isLoanInitedWithErrors, isCurrentUserCanChangeLoan, getUploadFileFunction],
  );

  const onDocumentItemDragStart = useCallback(
    e => {
      if (!isEditAllowed) {
        return;
      }

      return onDragStart(e);
    },
    [isEditAllowed, onDragStart],
  );

  const onDocumentItemDragEnd = useCallback(() => {
    if (!isEditAllowed) {
      return;
    }

    setDraggableData(null);
  }, [isEditAllowed, setDraggableData]);

  const renderDocumentItem = useCallback(
    (document, { typeId, name, isNested, children }) => {
      const {
        id,
        pagesCount,
        isDeleted,
        createdDate,
        isRejected,
        discriminator,
        isMalware,
        malwareScanTimeUtc,
        UploadSource: uploadSource,
        RecipientSignerName: recipientSignerName,
        companyId,
        createdByUserId,
      } = document;
      const isRecipientCopy = checkIsRecipientCopy(document);

      return (
        <div key={id}>
          <DocumentItem
            companyId={companyId}
            createdDate={createdDate}
            discriminator={discriminator}
            documentId={documentId}
            expandedList={expandedList}
            id={id}
            isActive={id === documentId}
            isCanBeDeleted={!checkIsDocumentUndeletable(document)}
            isDeleted={isDeleted}
            isDragging={isDragging}
            isMalware={isMalware}
            isNested={isNested}
            isRecipientCopiesExpanded={expandedList.includes(id)}
            isRecipientCopy={isRecipientCopy}
            isRejected={isRejected}
            isSelectable={isCopyDocumentsAllowed}
            loanParams={loanParams}
            malwareScanTimeUtc={malwareScanTimeUtc}
            name={name}
            pagesCount={pagesCount}
            recipientCopiesCount={children ? children.length : 0}
            recipientSignerName={recipientSignerName ?? null}
            toBeMerged={documentItemsToMerge.includes(id)}
            typeId={typeId}
            uploadSource={uploadSource}
            uploadedByUserId={createdByUserId}
            onCustomNavigateDocument={onCustomNavigateDocument}
            onDocumentItemClick={onDocumentItemClick}
            onDragEnd={onDocumentItemDragEnd}
            onDragStart={onDocumentItemDragStart}
            onOpenDocumentItemContextMenu={onOpenDocumentItemContextMenu}
            onToggleRecipientCopies={onToggleRecipientCopies}
          >
            {children}
          </DocumentItem>
        </div>
      );
    },
    [
      documentId,
      documentItemsToMerge,
      expandedList,
      isDragging,
      loanParams,
      onDocumentItemClick,
      onDocumentItemDragStart,
      onDocumentItemDragEnd,
      onCustomNavigateDocument,
      onOpenDocumentItemContextMenu,
      onToggleRecipientCopies,
      isCopyDocumentsAllowed,
    ],
  );

  const renderItems = useCallback(
    (types, { id: typeId, name }) =>
      groupRecipientCopiesByMaster(types).map(({ document, recipientCopies }) =>
        renderDocumentItem(document, {
          typeId,
          name,
          isNested: false,
          children: recipientCopies.length
            ? recipientCopies.map(copy =>
                renderDocumentItem(copy, { typeId, name, isNested: true, children: null }),
              )
            : null,
        }),
      ),
    [renderDocumentItem],
  );

  return (
    <>
      {items?.map(({ id, name, types }) => {
        const isRejectedType = id.startsWith(DOCUMENT_REJECTED_PLACEHOLDER);
        const isUploadVisible = isManageAllowed && !isLoanCanceled && !isRejectedType;
        const hasAiExtraction =
          !_.isNil(documentTypes[id]) &&
          !!_.get(documentTypes, [id, 'extractionSettings', 'extractionFields', 'length']);

        if (!types.length && !isEmptyTreeItemVisible) {
          return null;
        }

        const hasSelectedDoc = !!types.find(({ id }) => id === documentId);

        return (
          <LazyWrapper height={33} isDragging={isDragging} key={id} once={false}>
            {viewportRef => (
              <TreeItem
                id={id}
                itemId={id}
                label={
                  <ListItem
                    aria-label={name}
                    component="div"
                    data-testid={LoanApplicationDocumentsTestIds.getListItemTypeId(id)}
                    data-tree-node-type={LoanDocumentsTreeNodeType.DOCUMENT_TYPE}
                    key={id}
                    sx={classes.types}
                    onContextMenu={onOpenDocumentItemContextMenu}
                  >
                    <Box sx={classes.nameContainer}>
                      <Box sx={classes.nameRow}>
                        <Tooltip PopperProps={DOCUMENT_LABEL_TOOLTIP_POPPER_PROPS} title={name}>
                          <Box sx={classes.name}>{name}</Box>
                        </Tooltip>
                        {hasAiExtraction && (
                          <LabelCDS size={LabelSize.S} title="+AI" variant={LabelVariant.PURPLE} />
                        )}
                      </Box>
                    </Box>
                    <div className="flex items-center">
                      {isUploadVisible && (
                        <Box
                          aria-label="Upload new documents"
                          component="button"
                          data-testid={`documents-upload__upload-document-${id}`}
                          sx={classes.addTypeRedesign}
                          onClick={onOpenUploadDialog({
                            loanParams,
                            documentTypeId: id,
                            documentTypeName: name,
                          })}
                        >
                          +
                        </Box>
                      )}
                    </div>
                  </ListItem>
                }
                ref={viewportRef}
                sx={getTreeItemSx(hasSelectedDoc)}
                onDragLeave={onDragLeave}
                onDragOver={onDragOver}
                onDrop={onDragEnd}
              >
                {!!types?.length && (
                  <div data-testid={LoanApplicationDocumentsTestIds.getItemContainerId(id)}>
                    {renderItems(types, { id, name })}
                  </div>
                )}
              </TreeItem>
            )}
          </LazyWrapper>
        );
      })}
    </>
  );
};

DocumentTypeItem.propTypes = {
  loanParams: PropTypes.object.isRequired,
  setDraggableData: PropTypes.func.isRequired,
  setExpandedList: PropTypes.func.isRequired,
  expandedList: PropTypes.array.isRequired,
  documentItemsToMerge: PropTypes.arrayOf(PropTypes.string).isRequired,
  isDragging: PropTypes.bool.isRequired,
  isEmptyTreeItemVisible: PropTypes.bool.isRequired,
  isCopyDocumentsAllowed: PropTypes.bool.isRequired,
  onDocumentItemClick: PropTypes.func.isRequired,
  onDragStart: PropTypes.func.isRequired,
  onDragEnd: PropTypes.func.isRequired,
  onDragOver: PropTypes.func.isRequired,
  onDragLeave: PropTypes.func.isRequired,
  onToggleRecipientCopies: PropTypes.func.isRequired,
  items: PropTypes.array,
  documentId: PropTypes.string,
  onCustomNavigateDocument: PropTypes.func,
  onOpenDocumentItemContextMenu: PropTypes.func,
};

export default memo(DocumentTypeItem);
